// ═══════════════════════════════════════════════════════════════════
//  veeranshErrorBus — global pub/sub channel for errors that should
//  auto-open Veeransh to help the user.
//
//  Sources that emit:
//    • axios response interceptor (see setupAxiosVeeranshInterceptor)
//    • React error boundary
//    • any component that calls publishError({ message, ... })
//
//  Consumers:
//    • App.js subscribes and opens the Veeransh panel with the context
//
//  Debounced: the same error message within 5 s is ignored — avoids
//  spamming the user when a burst of failed requests happens.
// ═══════════════════════════════════════════════════════════════════

const listeners = new Set();
let lastKey = '';
let lastAt  = 0;

export function subscribeVeeranshError(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function publishError(err) {
  const key = `${err?.status || ''}|${err?.url || ''}|${err?.message || ''}`;
  const now = Date.now();
  if (key === lastKey && now - lastAt < 5000) return;
  lastKey = key; lastAt = now;
  listeners.forEach(fn => {
    try { fn(err); } catch {}
  });
}

/**
 * Wire an axios instance so only SERIOUS errors trigger publishError.
 * Veeransh auto-opens on:
 *   - 5xx (server errors — something actually broke)
 *   - Network errors (no response at all)
 * Silent on:
 *   - 4xx (client-side: 401 unauth, 404 not found, 409 conflict — routine)
 *   - Auth/OTP/security routes (user-driven, often expected fails)
 */
export function setupAxiosVeeranshInterceptor(axios) {
  axios.interceptors.response.use(
    (resp) => resp,
    (error) => {
      const url = error?.config?.url || '';
      const status = error?.response?.status || 0;

      // Skip routes where errors are expected (auth, OTP, polling, Veeransh itself)
      const silentUrl = /\/auth\/|\/security\/|\/veeransh\/|\/otp\b|\/saas-auth\/|\/phone\//i.test(url);

      // Only auto-open for real server problems — NOT for 4xx client errors.
      // 5xx = server bug. status === 0 means network/connection failure.
      const isServerError = status >= 500 || status === 0;

      if (!silentUrl && isServerError) {
        publishError({
          message: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Request failed',
          status,
          url,
          method: (error?.config?.method || 'get').toUpperCase(),
        });
      }
      return Promise.reject(error);
    }
  );
}
